/**
 * Guided character-creation form (task 20.3 / task 18.1).
 *
 * Posts to the create-character endpoint; server-side legality validation is
 * authoritative (SRD class/ancestry, ability-score method legality), so this
 * form collects the guided inputs in the shape the server requires — the shared
 * {@link CreateCharacterWireRequest} — and surfaces any rejection message rather
 * than duplicating the SRD rules.
 *
 * Ability scores are a METHOD + an assignment (not a flat map): the server
 * validates the assignment against the chosen SRD method.
 *   - standard_array — assign the fixed array 15,14,13,12,10,8 to the six abilities.
 *   - point_buy — assign 8–15 base scores summing to the 27-point buy.
 *   - rolled — the server rolls 4d6-drop-lowest and auto-assigns (no assignment sent).
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  AbilityKey,
  AbilityScoreMethod,
  CreateCharacterWireRequest,
} from "@rufus/shared-types";
import { useServices } from "../AppContext.js";
import { friendlyErrorMessage } from "../api-client.js";

const ABILITIES: readonly AbilityKey[] = [
  "str",
  "dex",
  "con",
  "int",
  "wis",
  "cha",
] as const;

/** The SRD standard array; the player assigns these six values to abilities. */
const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const;

/**
 * The SRD 5.1 ancestries + classes the server accepts (case-insensitive). These
 * mirror the server's validated sets — ancestries in
 * `@rufus/lambda` character-creation/srd-choices.ts (SRD_ANCESTRIES) and classes
 * in `@rufus/srd-content` (SrdClassName). Presented as pickers so a player can't
 * submit a value the server would reject with a 400. (Background is NOT
 * server-validated — free text.) Kept as literal lists here rather than a new
 * runtime dependency; they are small, stable SRD sets.
 */
const SRD_ANCESTRIES = [
  "Human",
  "Dwarf",
  "Elf",
  "Halfling",
  "Dragonborn",
  "Gnome",
  "Half-Elf",
  "Half-Orc",
  "Tiefling",
] as const;

const SRD_CLASSES = [
  "Barbarian",
  "Bard",
  "Cleric",
  "Druid",
  "Fighter",
  "Monk",
  "Paladin",
  "Ranger",
  "Rogue",
  "Sorcerer",
  "Wizard",
] as const;

export function CreateCharacterScreen() {
  const { api } = useServices();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", ancestry: "", class: "", background: "" });
  const [method, setMethod] = useState<AbilityScoreMethod>("standard_array");
  // The player's assignment of a score to each ability (used for
  // standard_array + point_buy; ignored for `rolled`, where the server rolls).
  const [scores, setScores] = useState<Record<AbilityKey, number>>({
    str: 15,
    dex: 14,
    con: 13,
    int: 12,
    wis: 10,
    cha: 8,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const abilityScores =
        method === "rolled"
          ? { method } // server rolls 4d6-drop-lowest and auto-assigns
          : { method, assignment: scores };
      const req: CreateCharacterWireRequest = {
        name: form.name,
        ancestry: form.ancestry,
        class: form.class,
        background: form.background,
        abilityScores,
      };
      const created = await api.createCharacter(req);
      navigate(`/characters/${created.characterId}`);
    } catch (err) {
      setError(friendlyErrorMessage(err, "Character creation failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="Create character">
      <h1>Create a character</h1>
      <form onSubmit={submit}>
        <label htmlFor="name">Name</label>
        <input
          id="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />

        <label htmlFor="ancestry">Ancestry</label>
        <select
          id="ancestry"
          value={form.ancestry}
          onChange={(e) => setForm({ ...form, ancestry: e.target.value })}
          required
        >
          <option value="">Select an ancestry…</option>
          {SRD_ANCESTRIES.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>

        <label htmlFor="class">Class</label>
        <select
          id="class"
          value={form.class}
          onChange={(e) => setForm({ ...form, class: e.target.value })}
          required
        >
          <option value="">Select a class…</option>
          {SRD_CLASSES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <label htmlFor="background">Background</label>
        <input
          id="background"
          value={form.background}
          onChange={(e) => setForm({ ...form, background: e.target.value })}
          required
        />

        <label htmlFor="score-method">Ability-score method</label>
        <select
          id="score-method"
          value={method}
          onChange={(e) => setMethod(e.target.value as AbilityScoreMethod)}
        >
          <option value="standard_array">
            Standard array ({STANDARD_ARRAY.join(", ")})
          </option>
          <option value="point_buy">Point buy (27 points)</option>
          <option value="rolled">Roll for me (4d6 drop lowest)</option>
        </select>

        {method !== "rolled" ? (
          <fieldset>
            <legend>
              Ability scores
              {method === "standard_array"
                ? ` — assign ${STANDARD_ARRAY.join(", ")}`
                : " — 8–15, spend 27 points"}
            </legend>
            {ABILITIES.map((a) => (
              <span key={a}>
                <label htmlFor={`score-${a}`}>{a.toUpperCase()}</label>
                <input
                  id={`score-${a}`}
                  type="number"
                  value={scores[a]}
                  onChange={(e) =>
                    setScores({
                      ...scores,
                      [a]: Number.parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </span>
            ))}
          </fieldset>
        ) : (
          <p>The server will roll your ability scores and assign them.</p>
        )}

        {error ? <p role="alert">{error}</p> : null}
        <button type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create"}
        </button>
      </form>
    </section>
  );
}
